import type { AutocompleteProps, AutocompleteRenderInputParams } from "@mui/material";
import { Autocomplete, createFilterOptions } from "@mui/material";
import type { Control } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import LabelComponent from "../LabelComponent";
import type { IRepresentativeFormValues, TRepresentativeValueNames } from "../Representatives/RepresentativeForm";
import StyledTextField from "../StyledTextField/StyledTextField";
import styles from "./style.module.scss";

interface IFreeAutocomplete extends Partial<AutocompleteProps<any, any, any, any>> {
    basicOptions: { value: any; label: string }[];
    control: Control<IRepresentativeFormValues, any>;
    controlName: TRepresentativeValueNames;
    required?: boolean;
    errors: any;
    placeholder?: string;
    label;
    labelStyle?: React.CSSProperties;
    chosenOption: { value: any; label: string };
    setChosenOption: (newValue: any) => void;
}

const filter = createFilterOptions<any>();

/**
 * Autocomplete where you can create a new option by typing something that doesn't yet exist.
 * Option must be of type {value, label: string}
 */
const FreeAutocomplete = (props: IFreeAutocomplete) => {
    const [t] = useTranslation();

    const {
        basicOptions,
        control,
        controlName,
        required,
        errors,
        placeholder,
        label,
        labelStyle,
        chosenOption,
        setChosenOption,
        ...rest
    } = props;

    const newValue =
        chosenOption && chosenOption.value !== "" && !basicOptions.find((item) => item.value === chosenOption.value);

    const defaultRenderInput = (params: AutocompleteRenderInputParams) => {
        return (
            <StyledTextField
                {...params}
                error={errors.controlName?.message ?? ""}
                placeholder={placeholder ?? ""}
                size="medium"
                InputProps={{
                    ...params.InputProps,
                    startAdornment: newValue ? (
                        <span className={styles["new-option-chip"]}>{t("Representatives.Form.New")}</span>
                    ) : undefined,
                }}
            />
        );
    };

    return (
        <div>
            <Controller
                name={controlName}
                control={control}
                rules={{ required }}
                render={(field) => {
                    return (
                        <>
                            {label && (
                                <LabelComponent htmlFor={controlName} required={required} style={labelStyle}>
                                    {label ?? ""}
                                </LabelComponent>
                            )}
                            <Autocomplete
                                className={styles["free-autocomplete"]}
                                size="small"
                                {...field}
                                {...rest}
                                value={chosenOption}
                                onChange={(event, newValue) => {
                                    if (typeof newValue === "string") {
                                        setChosenOption({
                                            label: newValue,
                                        });
                                    } else if (newValue && newValue.inputValue) {
                                        // Create a new value from the user input
                                        setChosenOption({
                                            value: newValue.inputValue,
                                            label: newValue.inputValue,
                                        });
                                    } else {
                                        setChosenOption(newValue);
                                    }
                                }}
                                filterOptions={(options, params) => {
                                    const filtered = filter(options, params);

                                    const { inputValue } = params;
                                    // Suggest the creation of a new value
                                    const isExisting = options.some((option) => inputValue === option.label);
                                    if (inputValue !== "" && !isExisting) {
                                        filtered.push({
                                            inputValue,
                                            label: `${t("Autocomplete.Add")} "${inputValue}"`,
                                        });
                                    }

                                    return filtered;
                                }}
                                handleHomeEndKeys
                                options={basicOptions}
                                getOptionLabel={(option) => {
                                    // Value selected with enter, right from the input
                                    if (typeof option === "string") {
                                        return option;
                                    }
                                    // Add "xxx" option created dynamically
                                    if (option.inputValue) {
                                        return option.inputValue;
                                    }
                                    // Regular option
                                    return option.label;
                                }}
                                renderOption={(props, option) => {
                                    return <li {...props}>{option.label}</li>;
                                }}
                                renderInput={defaultRenderInput}
                                componentsProps={{
                                    popper: {
                                        className: `free-autocomplete-popper`,
                                    },
                                }}
                            />
                        </>
                    );
                }}
            />
            <span className={styles["new-value-info"]}>
                {newValue ? t("Representatives.Form.NewRoleInfo") : t("Representatives.Form.RoleInfo")}
            </span>
        </div>
    );
};

export default FreeAutocomplete;
